import { useEffect, useRef } from "react";

import { APP_VERSION, fetchLatestVersion, shouldReload } from "./appVersion";

/** Default poll cadence for the deployed-version check. */
export const DEFAULT_VERSION_CHECK_INTERVAL_MS = 5 * 60_000; // 5 min

/**
 * Poll `/version.json` and invoke `onNewVersion(latestVersion)` once a newer build is detected.
 *
 * Checks on mount, on an interval, and whenever the tab regains
 * visibility/focus (so a user returning to a long-open tab picks up a deploy
 * promptly). Fires `onNewVersion` at most ONCE per page lifetime — the caller
 * decides what to do (reload, prompt) and a reload starts a fresh page anyway.
 *
 * Inert on unstamped local/dev builds (`APP_VERSION === ""`) — nothing to
 * compare against, and `/version.json` isn't emitted by the dev server.
 */
export function useVersionCheck(
  onNewVersion: (latest: string) => void,
  intervalMs: number = DEFAULT_VERSION_CHECK_INTERVAL_MS,
): void {
  const firedRef = useRef(false);
  const cbRef = useRef(onNewVersion);
  cbRef.current = onNewVersion;

  useEffect(() => {
    if (!APP_VERSION) return;

    let cancelled = false;

    const check = async () => {
      if (firedRef.current) return;
      const latest = await fetchLatestVersion();
      if (cancelled || firedRef.current) return;
      if (latest && shouldReload(APP_VERSION, latest)) {
        firedRef.current = true;
        cbRef.current(latest);
      }
    };

    const onWake = () => {
      if (document.visibilityState === "visible") void check();
    };

    void check();
    const id = window.setInterval(() => void check(), intervalMs);
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [intervalMs]);
}
